import React, { useState } from 'react';
import { AlertCircle, ShieldCheck, Volume2, X } from 'lucide-react';
import { api } from '../services/api';

interface SafetyTermsModalProps {
  onAccept: () => void;
}

export const SafetyTermsModal: React.FC<SafetyTermsModalProps> = ({ onAccept }) => {
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleStart = async () => {
    if (!hasAcceptedTerms || isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Record acceptances asynchronously via backend
      await Promise.all([
        api.terms.acceptTerms('1.0').catch(() => {}),
        api.terms.acknowledgeSafety('1.0').catch(() => {}),
      ]);
    } catch {
      // Non-blocking for offline
    } finally {
      setIsSubmitting(false);
      onAccept();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-xl p-4 select-none">
      <div className="relative w-full max-w-xl bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 shadow-[0_16px_48px_rgba(0,0,0,0.9)] text-slate-100 flex flex-col">
        {/* Header Branding */}
        <div className="flex items-center gap-2 mb-3">
          <span className="w-2.5 h-2.5 rounded-full bg-red-600 shadow-[0_0_8px_rgba(220,38,38,0.8)]" />
          <span className="font-mono text-[11px] font-bold tracking-[0.3em] uppercase text-cyan-400">
            REVIVE • SOUND SYSTEM
          </span>
        </div>

        <h2 className="text-2xl md:text-3xl font-black font-display text-white uppercase tracking-tight mb-2">
          Give Your EV a Voice.
        </h2>

        <p className="text-xs md:text-sm text-slate-300 font-sans leading-relaxed mb-4">
          The engine is synthesised live. Speed sets the pitch, effort sets the volume.
        </p>

        {/* Core Safety Guidelines Box */}
        <div className="bg-slate-950/80 border border-slate-800/80 rounded-2xl p-4 mb-4 flex flex-col gap-2.5 text-xs text-slate-300 font-sans">
          <div className="flex items-start gap-2.5">
            <Volume2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
            <span>REVIVE is built for an EV&apos;s screen.</span>
          </div>
          <div className="flex items-start gap-2.5">
            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
            <span>Location is what measures your speed (processed strictly locally).</span>
          </div>
          <div className="flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <span>Always keep your attention on the road.</span>
          </div>
        </div>

        {/* Safety Undertaking Disclaimer */}
        <div className="text-[11px] font-sans text-slate-400 leading-relaxed border-l-2 border-amber-500/60 pl-3 mb-5">
          By starting, I undertake to keep my eyes on the road, to touch the screen only when stopped,
          and to obey traffic law. The speed shown is indicative and does not replace the vehicle&apos;s own
          speedometer.
        </div>

        {/* Terms Acceptance Checkbox */}
        <label className="flex items-center gap-3 cursor-pointer select-none mb-6 group">
          <input
            type="checkbox"
            checked={hasAcceptedTerms}
            onChange={(e) => setHasAcceptedTerms(e.target.checked)}
            className="w-5 h-5 rounded border-slate-700 bg-slate-950 text-cyan-500 focus:ring-0 focus:ring-offset-0 cursor-pointer accent-cyan-500"
          />
          <span className="text-xs text-slate-300 font-medium">
            I accept the{' '}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setShowTermsModal(true);
              }}
              className="text-cyan-400 underline hover:text-cyan-300 font-semibold inline"
            >
              Terms and Conditions
            </button>
            .
          </span>
        </label>

        {/* Primary Start Button */}
        <button
          type="button"
          onClick={handleStart}
          disabled={!hasAcceptedTerms || isSubmitting}
          className={`w-full py-4 rounded-2xl font-display font-black text-sm md:text-base tracking-[0.2em] uppercase transition-all duration-200 cursor-pointer ${
            hasAcceptedTerms && !isSubmitting
              ? 'bg-gradient-to-r from-red-600 via-rose-600 to-red-700 text-white shadow-[0_0_24px_rgba(220,38,38,0.5)] hover:brightness-110 active:scale-[0.99]'
              : 'bg-slate-800/60 text-slate-500 border border-slate-800 cursor-not-allowed'
          }`}
        >
          {isSubmitting ? 'INITIALIZING...' : 'START REVIVE'}
        </button>
      </div>

      {/* Embedded Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/90 p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-lg w-full max-h-[80vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-4">
              <h3 className="font-display font-bold text-white text-base">
                Terms & Conditions (v1.0)
              </h3>
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto pr-2 text-xs text-slate-300 font-sans leading-relaxed space-y-3">
              <p>
                <strong>1. ROAD SAFETY FIRST:</strong> Drivers must keep their eyes on the road at all times. Touch controls are designed strictly for interaction while the vehicle is safely parked or stationary.
              </p>
              <p>
                <strong>2. INDICATIVE SPEED ONLY:</strong> Speed displayed in REVIVE is measured via your device GPS for acoustic sound modulation only. It does NOT replace your vehicle&apos;s certified primary speedometer.
              </p>
              <p>
                <strong>3. ORIGINAL ACOUSTIC SYNTHESIS:</strong> All soundscapes in REVIVE are original procedural synthetic designs. REVIVE is not affiliated with, endorsed by, or using copyrighted recordings of any vehicle manufacturer.
              </p>
              <p>
                <strong>4. DATA MINIMISATION & PRIVACY:</strong> Your location is processed locally in your browser to modulate sound dynamics. REVIVE does not record or upload your driving routes or trip history.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setHasAcceptedTerms(true);
                setShowTermsModal(false);
              }}
              className="mt-5 w-full py-2.5 rounded-xl bg-cyan-600 text-white font-mono font-bold text-xs uppercase"
            >
              I Understand & Agree
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
